import { useState } from 'react';
import clsx from 'clsx';
import { Icon } from '../../../layout/Icon.jsx';
import { Pill } from '../../../layout/Pill.jsx';
import {
  useWarehouses,
  useCreateWarehouse,
  useUpdateWarehouse,
  useDeleteWarehouse
} from '../hooks/useCategories.js';

const TYPES = [
  { key: 'cafeteria', label: 'كافتيريا', emoji: '🍕' },
  { key: 'toys', label: 'ألعاب', emoji: '🧸' },
  { key: 'general', label: 'عام', emoji: '📦' }
];

const EMPTY = { name: '', key: '', type: 'general', isActive: true };

// الـ backend يفرض ^[a-z0-9_-]+$ بطول 2..40. الأسماء العربية لا تمر، لذا نضمن
// fallback آمن عبر طابع زمني بـ base36 إن لم يتبقَّ من الاسم أحرف ASCII صالحة.
function generateWarehouseKey(name) {
  const ascii = (name || '')
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9_-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40);
  if (ascii.length >= 2) return ascii;
  return `wh-${Date.now().toString(36)}`;
}

export function WarehousesModal({ open, onClose }) {
  const { data: warehouses = [], isLoading } = useWarehouses();
  const createW = useCreateWarehouse();
  const updateW = useUpdateWarehouse();
  const deleteW = useDeleteWarehouse();

  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  if (!open) return null;

  function reset() {
    setForm(EMPTY);
    setEditingId(null);
  }

  function startEdit(w) {
    setEditingId(w._id || w.id);
    setForm({
      name: w.name || '',
      key: w.key || '',
      type: w.type || 'general',
      isActive: w.isActive !== false
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) return;
    const body = {
      name: form.name.trim(),
      key: form.key.trim() || generateWarehouseKey(form.name),
      type: form.type,
      isActive: form.isActive
    };
    if (editingId) {
      await updateW.mutateAsync({ id: editingId, body }, { onSuccess: reset });
    } else {
      await createW.mutateAsync(body, { onSuccess: reset });
    }
  }

  async function handleDelete() {
    if (!confirmDelete) return;
    await deleteW.mutateAsync(confirmDelete._id || confirmDelete.id, {
      onSuccess: () => setConfirmDelete(null)
    });
  }

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4"
    >
      <div className="bg-white rounded-[14px] shadow-pop w-full max-w-[760px] max-h-[88vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-line">
          <div>
            <h3 className="font-extrabold text-[16px] m-0">إدارة المخازن</h3>
            <div className="text-ink-3 text-[12px] mt-0.5">
              عرّف المخازن قبل إضافة المنتجات والأقسام
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-ink-3 hover:text-ink p-1"
            aria-label="إغلاق"
          >
            <Icon name="x" size={18} />
          </button>
        </div>

        <div className="grid gap-4 p-5 overflow-auto" style={{ gridTemplateColumns: '1fr 280px' }}>
          {/* List */}
          <div className="space-y-2">
            <div className="text-[11.5px] font-semibold text-ink-3 uppercase tracking-wider">
              المخازن الحالية ({warehouses.length})
            </div>
            {isLoading ? (
              <div className="text-ink-3 text-[13px] py-6 text-center">جاري التحميل...</div>
            ) : warehouses.length === 0 ? (
              <div className="text-ink-3 text-[13px] py-6 text-center bg-paper-2 rounded-[10px]">
                لا توجد مخازن — أنشئ أول مخزن من اليسار
              </div>
            ) : (
              warehouses.map((w) => {
                const id = w._id || w.id;
                const t = TYPES.find((x) => x.key === w.type) || TYPES[2];
                const editing = editingId === id;
                return (
                  <div
                    key={id}
                    className={clsx(
                      'flex items-center gap-3 p-3 rounded-[10px] border transition-colors',
                      editing ? 'border-orange bg-orange-soft/40' : 'border-line bg-white hover:bg-paper-2'
                    )}
                  >
                    <div className="text-[22px]">{t.emoji}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-[13.5px]">{w.name}</span>
                        <Pill variant={w.isActive ? 'success' : 'muted'}>
                          {w.isActive ? 'فعّال' : 'معطّل'}
                        </Pill>
                      </div>
                      <div className="text-ink-3 text-[11px] mt-0.5">
                        النوع: {t.label} · المعرّف: <code className="font-mono">{w.key}</code>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => startEdit(w)}
                      >
                        <Icon name="edit" size={12} /> تعديل
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm text-red hover:bg-red/5"
                        onClick={() => setConfirmDelete(w)}
                      >
                        <Icon name="trash" size={12} />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-3 bg-paper-2 rounded-[10px] p-4 self-start">
            <div className="text-[11.5px] font-semibold text-ink-3 uppercase tracking-wider">
              {editingId ? 'تعديل مخزن' : 'مخزن جديد'}
            </div>

            <div>
              <label className="block text-[12px] font-semibold mb-1">الاسم</label>
              <input
                className="input text-[13px]"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="مثال: مخزن الكافتيريا"
                required
              />
            </div>

            <div>
              <label className="block text-[12px] font-semibold mb-1">النوع</label>
              <div className="flex flex-wrap gap-1.5">
                {TYPES.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setForm({ ...form, type: t.key })}
                    className={clsx(
                      'px-3 py-1.5 rounded-[8px] text-[12px] font-semibold border transition-colors',
                      form.type === t.key
                        ? 'bg-orange text-white border-orange'
                        : 'bg-white border-line hover:bg-paper-2'
                    )}
                  >
                    {t.emoji} {t.label}
                  </button>
                ))}
              </div>
            </div>

            <label className="flex items-center gap-2 text-[12.5px] cursor-pointer">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              />
              فعّال
            </label>

            <div className="flex items-center gap-2 pt-2">
              <button
                type="submit"
                className="btn btn-primary flex-1"
                disabled={createW.isPending || updateW.isPending}
              >
                {editingId ? 'حفظ' : 'إضافة'}
              </button>
              {editingId ? (
                <button type="button" className="btn" onClick={reset}>
                  إلغاء
                </button>
              ) : null}
            </div>
          </form>
        </div>

        <div className="border-t border-line px-5 py-3 flex justify-end">
          <button type="button" className="btn" onClick={onClose}>
            إغلاق
          </button>
        </div>
      </div>

      {/* Delete confirm */}
      {confirmDelete ? (
        <div
          className="fixed inset-0 bg-black/50 z-[60] grid place-items-center p-4"
        >
          <div className="bg-white rounded-[14px] shadow-pop w-full max-w-[400px] p-5">
            <h4 className="font-extrabold text-[15px] mb-2">حذف مخزن</h4>
            <p className="text-[13px] text-ink-2 mb-4">
              هل أنت متأكد من حذف "{confirmDelete.name}"؟ لا يمكن الحذف إن وُجدت فيه منتجات أو أقسام.
            </p>
            <div className="flex justify-end gap-2">
              <button type="button" className="btn" onClick={() => setConfirmDelete(null)}>
                إلغاء
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={deleteW.isPending}
              >
                حذف
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
